import { QueryClient, QueryFunction } from "@tanstack/react-query";

const TOKEN_KEY = "token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getAuthHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Called only when the /api/auth/user check confirms the session is gone.
// Clears the stored token and redirects to login.
export function handleUnauthorized() {
  clearToken();
  queryClient.clear();
  window.location.replace("/login");
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<any> {
  const isFormData = data instanceof FormData;

  const res = await fetch(url, {
    method,
    headers: {
      ...(data && !isFormData ? { "Content-Type": "application/json" } : {}),
      ...getAuthHeaders(),
    },
    body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
  });

  if (!res.ok) {
    if (res.status === 401) {
      // Throw an error so mutations surface "Enhancement failed" toast
      // rather than silently logging the user out.
      throw new Error("401: Unauthorized");
    }
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }

  if (res.status === 204) {
    return null;
  }

  const response = await res.json();

  if (response && typeof response === "object" && "success" in response) {
    if (response.success) {
      return response.data;
    } else {
      throw new Error(response.error?.message || "API Error");
    }
  }

  return response;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      headers: getAuthHeaders(),
    });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") {
        // The /api/auth/user endpoint: just signal "not logged in" without
        // triggering a redirect (the Router handles this case via isAuthenticated).
        return null;
      }
      // All other authenticated queries: treat 401 as a session expiry and
      // force the user back to login immediately.
      handleUnauthorized();
      return null;
    }

    if (!res.ok) {
      const text = (await res.text()) || res.statusText;
      throw new Error(`${res.status}: ${text}`);
    }

    const response = await res.json();

    if (response && typeof response === "object" && "success" in response) {
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.error?.message || "API Error");
      }
    }

    return response;
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
